import { HubConnection } from '@microsoft/signalr';
import { useBunkerContext } from 'src/context/BunkerContext';
import { mapBunkeringBM, mapBunkeringStatusBM, mapFuelRobBMs, mapBunkerReceivedBMs, mapBunkerLossBMs } from 'src/factory/Bunkering/BunkeringFactory';
import { BunkeringBM } from 'src/types/BusinessModel/Bunkering/BunkeringBM';
import { useToast } from './useToast';
import { BunkeringStatusBM } from 'src/types/BusinessModel/Bunkering/BunkeringStatusBM';
import { getLatestStatus } from 'src/components/bunkering/BunkeringUtils';
import { FuelRobBM } from 'src/types/BusinessModel/Bunkering/FuelRobBM';
import { BunkerReceivedBM } from 'src/types/BusinessModel/Bunkering/BunkerReceivedBM';
import { BunkerLossBM } from 'src/types/BusinessModel/Bunkering/BunkerLossBM';
import { BunkeringStatus } from 'src/types/ViewModel/Bunkering/BunkeringStatus';
import { BunkeringStatusEnum } from 'src/types/ViewModel/Bunkering/BunkeringEvent';
import { UUID } from 'crypto';

export const useBunkerUpdateHandler = () => {
    const { toastSuccess } = useToast();
    const { bunkerings, setBunkerings, bunkeringEvents, fuelRobData, setFuelRobData, deletedBunkerings, setDeletedBunkerings, bunkerLossData, setBunkerLossData } =
        useBunkerContext();

    const handleUpdatedBunkering = (bm: BunkeringBM) => {
        const _bunkerings = bunkerings ? [...bunkerings] : [];

        // If the bunkering is deleted, remove it from the list of bunkerings related to deleted events
        if (bm.deleted) {
            const _deletedBunkerings = deletedBunkerings.filter((b) => b.bunkeringGuid !== bm.bunkeringGuid);
            setDeletedBunkerings(_deletedBunkerings);
        }

        const bunkeringEvent = bunkeringEvents.find((be) => be.eventID === bm.eventId);
        if (!bunkeringEvent) return;
        const index = _bunkerings.findIndex((b) => b.bunkeringGuid === bm.bunkeringGuid);
        let mappedBunkering = mapBunkeringBM(bm, bunkeringEvent);

        // Inject synthetic "Registered" status for new bunkerings awaiting their first backend status
        if (bm.bunkeringId === 0 && (bm.bunkeringStatuses?.length ?? 0) === 0) {
            const syntheticStatus: BunkeringStatus = {
                bunkeringStatusId: 0,
                bunkeringStatusGuid: crypto.randomUUID() as UUID,
                bunkeringId: 0,
                bunkeringGuid: bm.bunkeringGuid,
                status: BunkeringStatusEnum.Registered,
                activeFromDate: new Date(),
                updatedBy: bm.lastUpdatedBy ?? '',
                updatedAt: new Date(),
            };
            mappedBunkering = {
                ...mappedBunkering,
                bunkeringStatuses: [syntheticStatus],
                currentStatus: syntheticStatus,
            };
        }

        if (index >= 0) {
            _bunkerings[index] = mappedBunkering;
        } else {
            _bunkerings.push(mappedBunkering);
        }
        setBunkerings(_bunkerings);
    };

    const handleUpdatedBunkeringStatus = (bm: BunkeringStatusBM) => {
        const updatedStatus = mapBunkeringStatusBM(bm);
        const _bunkerings = bunkerings ? [...bunkerings] : [];
        const index = _bunkerings.findIndex((b) => b.bunkeringGuid === bm.bunkeringGuid);
        if (index >= 0) {
            const _bunkering = _bunkerings[index];
            const existingStatus = _bunkering.bunkeringStatuses.find((s) => s.bunkeringStatusId === bm.bunkeringStatusId);
            if (existingStatus) {
                Object.assign(existingStatus, updatedStatus);
            } else {
                _bunkering.bunkeringStatuses.push(updatedStatus);
            }
            const latestStatus = getLatestStatus(_bunkering.bunkeringStatuses);
            _bunkering.currentStatus = latestStatus;
            setBunkerings(_bunkerings);
        }
    };

    const handleDeletedBunkeringStatus = (bunkeringGuid: string) => {
        const _bunkerings = bunkerings ? [...bunkerings] : [];
        const index = _bunkerings.findIndex((b) => b.bunkeringStatuses.some((s) => s.bunkeringStatusGuid === bunkeringGuid));
        if (index >= 0) {
            const _bunkering = _bunkerings[index];
            _bunkering.bunkeringStatuses = _bunkering.bunkeringStatuses.filter((s) => s.bunkeringStatusGuid !== bunkeringGuid);
            const latestStatus = getLatestStatus(_bunkering.bunkeringStatuses);
            _bunkering.currentStatus = latestStatus;
            setBunkerings(_bunkerings);
        }
    };

    const handleUpdatedRobs = (bms: FuelRobBM[], offlineFuelRobs: any[]) => {
        const updatedFuelRobData = mapFuelRobBMs(bms, offlineFuelRobs);
        const _fuelRobData = fuelRobData ? [...fuelRobData] : [];
        updatedFuelRobData.forEach((fuelRob) => {
            const index = _fuelRobData.findIndex(
                (rob) => rob.noonReportId === fuelRob.noonReportId && rob.bunkeringGuid === fuelRob.bunkeringGuid && rob.tankId === fuelRob.tankId
            );
            if (index >= 0) {
                _fuelRobData[index] = fuelRob;
            } else {
                _fuelRobData.push(fuelRob);
            }
        });
        setFuelRobData(_fuelRobData);
    };

    // Upserting a bunker loss only sends/returns the losses for a single noon report (the one being
    // edited). Unlike a wholesale replace, this merges the response into the existing bunkerLossData
    // context array by removing only that noonReportId's prior entries first - preserving loss data
    // for every other noon report/entry (e.g. a different watch on the same day) already loaded into
    // context. Without this, saving a loss for one entry would wipe out previously-loaded losses for
    // any other entry until a full page reload/refetch.
    const handleUpdatedBunkerLoss = (bms: BunkerLossBM[], noonReportId: number) => {
        const updatedBunkerLossData = mapBunkerLossBMs(bms);
        const _bunkerLossData = (bunkerLossData ?? []).filter((loss) => loss.noonReportId !== noonReportId);
        setBunkerLossData([..._bunkerLossData, ...updatedBunkerLossData]);
    };

    const handleUpdatedDebunkering = (bms: BunkerReceivedBM[]) => {
        if (!Array.isArray(bms)) return;

        const _bunkerings = bunkerings ? [...bunkerings] : [];

        const grouped = new Map<string, BunkerReceivedBM[]>();
        for (const bm of bms) {
            const group = grouped.get(bm.bunkeringGuid) ?? [];
            group.push(bm);
            grouped.set(bm.bunkeringGuid, group);
        }

        grouped.forEach((items, bunkeringGuid) => {
            const index = _bunkerings.findIndex((b) => b.bunkeringGuid === bunkeringGuid);
            if (index < 0) return;

            const incomingEventIds = items
                .map((bm) => bm.debunkeringEventId)
                .filter((id): id is string => id !== undefined);

            const existing = _bunkerings[index].debunkeringData ?? [];
            const filtered = existing.filter((d) => !incomingEventIds.includes(d.debunkeringEventId ?? ''));
            const mapped = mapBunkerReceivedBMs(items);

            _bunkerings[index] = {
                ..._bunkerings[index],
                debunkeringData: [...filtered, ...mapped],
            };
        });

        setBunkerings(_bunkerings);
    };

    const toggleBunkerToast = (signalRResponse: any, hubConnection: HubConnection, dataType: string) => {
        if (signalRResponse.connectionId !== hubConnection.connectionId) {
            if (signalRResponse.sender) {
                toastSuccess(signalRResponse.sender + ' has updated ' + dataType);
            } else {
                toastSuccess(dataType + ' has been updated');
            }
        }
    };

    return {
        handleUpdatedBunkering,
        handleUpdatedBunkeringStatus,
        handleDeletedBunkeringStatus,
        handleUpdatedRobs,
        handleUpdatedBunkerLoss,
        handleUpdatedDebunkering,
        toggleBunkerToast,
    };
};
